import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ChevronRight, Play, Plus, RotateCcw, Square, Terminal, Trash2 } from 'lucide-react';
import { api, type DashboardState, type TenantDetail } from '../api';
import { Button } from '../components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Skeleton } from '../components/ui/skeleton';
import { Badge } from '../components/ui/badge';
import {
  EmptyState,
  ErrorNote,
  LiveBar,
  Panel,
  PageHeader,
  StatusDot,
  billingLabel,
  cost,
  costTitle,
  since,
} from '../components/kit';
import { Count, RunTranscript, TaskAttachments } from './runs/shared';
import { ScopeStrip } from './runs/scope';
import { IssuePicker, NewTaskDialog } from './runs/intake';
import { Ledger } from './runs/ledger';
import { Failures } from './runs/failures';
import { IdleWarning, Workers } from './runs/workers';

export function RunsPage({ tenant }: { tenant: string | null }) {
  const client = useQueryClient();
  const suffix = tenant ? `?tenant=${tenant}` : '';
  const dashboard = useQuery({
    queryKey: ['dashboard', tenant],
    queryFn: () => api.get<DashboardState>(`/api/dashboard${suffix}`),
    refetchInterval: 4000,
  });
  const tenants = useQuery({ queryKey: ['tenants'], queryFn: () => api.get<TenantDetail[]>('/api/tenants') });
  const refresh = () => client.invalidateQueries({ queryKey: ['dashboard'] });

  const [transcriptFor, setTranscriptFor] = useState<string | null>(null);
  const [feedbackFor, setFeedbackFor] = useState<string | null>(null);
  const [newTaskOpen, setNewTaskOpen] = useState(false);
  // The open surface lives in the URL: a queue you are working through survives
  // a refresh, and "look at this run" is a link somebody can send.
  const [params, setParams] = useSearchParams();
  const tab = params.get('tab') ?? 'flight';

  const state = dashboard.data;
  const projects = (tenants.data ?? [])
    .filter((entry) => !tenant || entry.id === tenant)
    .flatMap((entry) =>
      entry.projects.map((project) => ({
        ...project,
        tenantId: entry.id,
        // A repository whose tenant has no handler cannot run anything queued
        // against it, and the dialog says so before somebody writes a brief.
        handlers: entry.handlers.map((handler) => handler.status),
      })),
    );

  const act = useMutation({
    mutationFn: (path: string) => api.post(path),
    onSuccess: refresh,
  });
  const remove = useMutation({
    mutationFn: (taskId: string) => api.del(`/api/tasks/${taskId}`),
    onSuccess: refresh,
  });

  return (
    <>
      <PageHeader
        title="Runs"
        description="Every agent run this installation has started, what it drew on, and what it produced."
        actions={
          <>
            <IssuePicker tenant={tenant} onStarted={refresh} />
            <Button onClick={() => setNewTaskOpen(true)}>
              <Plus className="size-3.5" aria-hidden /> New task
            </Button>
          </>
        }
      />

      {state && <ScopeStrip state={state} />}

      <NewTaskDialog
        open={newTaskOpen}
        projects={projects}
        onClose={() => setNewTaskOpen(false)}
        onCreated={() => {
          setNewTaskOpen(false);
          setParams({ ...(tenant ? { tenant } : {}), tab: 'queue' });
          refresh();
        }}
      />

      <Tabs
        value={tab}
        onValueChange={(next) => setParams(next === 'flight' ? {} : { tab: next }, { replace: true })}
      >
        <TabsList variant="line" className="mb-1 w-full justify-start overflow-x-auto">
          <TabsTrigger value="flight" className="flex-none gap-2">
            In flight
            <Count value={state?.running.length} live={(state?.running.length ?? 0) > 0} />
          </TabsTrigger>
          <TabsTrigger value="queue" className="flex-none gap-2">
            Queue
            <Count value={state?.queue.length} />
          </TabsTrigger>
          <TabsTrigger value="ledger" className="flex-none">
            Ledger
          </TabsTrigger>
          <TabsTrigger value="usage" className="flex-none">
            Usage
          </TabsTrigger>
          <TabsTrigger value="workers" className="flex-none gap-2">
            Workers
            <Count value={state?.workers?.length} />
          </TabsTrigger>
        </TabsList>

        <TabsContent value="flight">
      <Panel description="Runs happening right now. Open the transcript to watch the agent work.">
        {!state ? (
          <Skeleton className="h-14 w-full" />
        ) : state.running.length === 0 ? (
          <EmptyState
            title="Nothing running"
            hint="The worker picks up queued tasks on its next poll, or you can start one below."
          />
        ) : (
          <div className="-my-1 divide-y divide-rule">
            {state.running.map((run) => (
              <div key={run.id} className="py-3 first:pt-1 last:pb-1">
                <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between sm:gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="flex items-center gap-2 text-[13.5px]">
                      <StatusDot state={run.cancelRequested ? 'stopping' : 'running'} />
                      <span className="data font-semibold whitespace-nowrap text-ink">{run.projectKey}</span>
                      <span className="truncate font-medium">{run.title}</span>
                    </p>
                    <p className="data mt-1 truncate text-ink-3" title={run.worktree}>
                      {run.worktree}
                    </p>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    <Badge variant={run.cancelRequested ? 'warn' : 'run'}>
                      {run.cancelRequested ? 'stopping…' : `running ${since(run.startedAt)}`}
                    </Badge>
                    <Button
                      variant="outline"
                      size="sm"
                      aria-expanded={transcriptFor === run.id}
                      onClick={() => setTranscriptFor(transcriptFor === run.id ? null : run.id)}
                    >
                      <Terminal className="size-3.5" aria-hidden /> Transcript
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={run.cancelRequested}
                      onClick={() => act.mutate(`/api/runs/${run.id}/cancel`)}
                    >
                      <Square className="size-3.5" aria-hidden /> {run.cancelRequested ? 'Stopping' : 'Stop'}
                    </Button>
                  </div>
                </div>
                <div className="mt-2.5">
                  <LiveBar label={`${run.title} is running`} />
                </div>
                {transcriptFor === run.id && <RunTranscript id={run.id} live />}
              </div>
            ))}
          </div>
        )}
      </Panel>
        </TabsContent>

        <TabsContent value="queue">
      {state && <IdleWarning workers={state.workers ?? []} queued={state.queue.length} />}
      <Panel description="Tasks waiting for a free slot. The worker takes the oldest first; start one now, or hand it back.">
        {!state ? (
          <Skeleton className="h-14 w-full" />
        ) : state.queue.length === 0 ? (
          <EmptyState title="Queue empty" hint="Add a task below, or import one from a tracker." />
        ) : (
          <div className="-my-1 divide-y divide-rule">
            {state.queue.map((task) => (
              <div key={task.id} className="py-3 first:pt-1 last:pb-1">
                <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between sm:gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="flex items-center gap-2 text-[13.5px]">
                      <StatusDot state="queued" />
                      <span className="data font-semibold whitespace-nowrap text-ink">{task.projectKey}</span>
                      <span className="truncate">{task.title}</span>
                    </p>
                    <p className="mt-1">
                      <Badge variant="secondary">{task.status}</Badge>
                    </p>
                    {(task.body || task.notes) && (
                      <details className="group mt-2">
                        <summary className="label-caps flex w-fit cursor-pointer list-none items-center gap-1 hover:text-ink [&::-webkit-details-marker]:hidden">
                          <ChevronRight className="size-3 transition-transform duration-150 group-open:rotate-90" aria-hidden />
                          Detail
                        </summary>
                        <pre className="mt-2 max-w-[80ch] rounded-[5px] border border-rule bg-raised px-3 py-2 font-mono text-[11.5px] whitespace-pre-wrap text-ink-2">
                          {[task.body, task.notes].filter(Boolean).join('\n\n---\n\n')}
                        </pre>
                        <TaskAttachments taskId={task.id} />
                      </details>
                    )}
                  </div>
                  <div className="flex shrink-0 gap-2">
                    <Button size="sm" onClick={() => act.mutate(`/api/tasks/${task.id}/run`)}>
                      <Play className="size-3.5" aria-hidden /> Run now
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => act.mutate(`/api/tasks/${task.id}/release`)}
                    >
                      <RotateCcw className="size-3.5" aria-hidden /> Release
                    </Button>
                    <Button variant="ghost" size="icon-sm" onClick={() => remove.mutate(task.id)}>
                      <Trash2 className="size-3.5" aria-hidden />
                      <span className="sr-only">Delete this task</span>
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
        <ErrorNote error={act.error} />
      </Panel>
        </TabsContent>

        <TabsContent value="ledger">
          <Ledger
            tenant={tenant}
            runId={params.get('run')}
            onClearRun={() => setParams({ tab: 'ledger' }, { replace: true })}
            projects={projects.map((project) => project.key)}
            transcriptFor={transcriptFor}
            feedbackFor={feedbackFor}
            onTranscript={setTranscriptFor}
            onFeedback={setFeedbackFor}
            onSent={() => {
              setFeedbackFor(null);
              refresh();
            }}
          />
        </TabsContent>

        <TabsContent value="usage">
      <Panel
        description="Measured from run transcripts, last 24 hours. A run billed to a Claude subscription costs nothing per run — its figure is what the same work would have cost at API list price. Plan limits are not exposed by the Claude CLI."
      >
        {!state || state.usage.length === 0 ? (
          <EmptyState title="No completed runs in the last 24 hours" />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Model</TableHead>
                <TableHead>Billed to</TableHead>
                <TableHead className="text-right">Runs</TableHead>
                <TableHead className="text-right">Tokens</TableHead>
                <TableHead className="text-right">Cost</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {state.usage.map((row) => (
                <TableRow key={`${row.model}-${row.billing}`}>
                  <TableCell className="data text-ink">{row.model || 'unknown'}</TableCell>
                  <TableCell>
                    <Badge variant={row.billing === 'api' ? 'warn' : 'secondary'}>
                      {billingLabel(row.billing)}
                    </Badge>
                  </TableCell>
                  <TableCell className="data text-right">{row.runs}</TableCell>
                  <TableCell className="data text-right">{row.tokens.toLocaleString('en-US')}</TableCell>
                  <TableCell className="data text-right" title={costTitle(row.billing)}>
                    {cost(row.costUsd, row.billing)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Panel>
      <Failures failures={state?.failures} />
        </TabsContent>

        <TabsContent value="workers">
          <Workers workers={state?.workers} isAdmin={state?.viewer.isAdmin ?? false} onChanged={refresh} />
        </TabsContent>
      </Tabs>
    </>
  );
}

/** A worker is late when it has missed several heartbeats, not one. */
